import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { adminJournalAPI } from '../lib/api';
import { toast } from 'sonner';
import { RefreshCw, ShieldCheck, ShieldAlert, Mail, Receipt, AlertTriangle, FlaskConical, Download } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Link } from 'react-router-dom';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const eur = (v, c) => (v == null ? '—' : `${v.toFixed(2).replace('.', ',')} ${c || 'EUR'}`);
const TYPE_LABEL = { abonnement: 'Mensuel 29,99 €', abonnement_annuel: 'Annuel 299 €', one_shot: 'Achat unique', '': '—' };

const VerifBadge = ({ v }) => {
  const map = { VERIFIED: ['bg-emerald-100 text-emerald-800', 'Vérifiée PayPal'], INVALID: ['bg-red-100 text-red-800', 'Rejetée (INVALID)'], UNVERIFIED: ['bg-amber-100 text-amber-800', 'Non vérifiable'], PENDING: ['bg-slate-100 text-slate-700', 'En attente'] };
  const [cls, label] = map[v] || map.PENDING;
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
};

const EMAIL_STATUS = {
  requested: ['bg-slate-100 text-slate-700', 'Demandé — en cours'],
  accepted: ['bg-sky-100 text-sky-800', 'Accepté par Brevo — délivrance non confirmée'],
  delivered: ['bg-emerald-100 text-emerald-800', 'Délivré (confirmé)'],
  failed: ['bg-red-100 text-red-800', 'Échec d\'envoi'],
  bounced: ['bg-red-100 text-red-800', 'Rejeté (adresse refusée / bounce)'],
  abandoned: ['bg-red-200 text-red-900', 'Échec persistant — alerte admin'],
  deferred: ['bg-amber-100 text-amber-800', 'Différé par Brevo'],
  pending: ['bg-slate-100 text-slate-700', 'Inconnu / en attente'],
};
const EmailBadge = ({ s }) => { const [cls, label] = EMAIL_STATUS[s] || EMAIL_STATUS.pending; return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>; };

const TONES = { slate: 'bg-slate-50 border-slate-200', emerald: 'bg-emerald-50 border-emerald-200', red: 'bg-red-50 border-red-300', amber: 'bg-amber-50 border-amber-200', sky: 'bg-sky-50 border-sky-200' };
const Stat = ({ label, value, tone = 'slate', testId }) => (
  <div className={`rounded-lg border p-3 ${TONES[tone] || TONES.slate}`} data-testid={testId}>
    <div className="text-2xl font-bold tabular-nums text-slate-900">{value ?? 0}</div>
    <div className="text-xs text-slate-600">{label}</div>
  </div>
);


const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

const CsvExport = ({ mode }) => {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await adminJournalAPI.exportCsv(from, to, mode);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a'); a.href = url; a.download = `monae_paypal_${from}_${to}_${mode}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success('Export CSV généré');
    } catch { toast.error('Export impossible'); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border bg-slate-50 p-3 space-y-2" data-testid="csv-export">
      <div className="flex flex-wrap items-end gap-2">
        <div><label className="text-xs text-slate-600" htmlFor="csv-from">Du</label><Input id="csv-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="csv-from" /></div>
        <div><label className="text-xs text-slate-600" htmlFor="csv-to">Au</label><Input id="csv-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="csv-to" /></div>
        <Button size="sm" onClick={download} disabled={busy || !from || !to} data-testid="csv-download-btn"><Download className="h-4 w-4 mr-1" />{busy ? 'Génération…' : `Exporter CSV (${mode === 'all' ? 'tous' : mode.toUpperCase()})`}</Button>
      </div>
      <p className="text-[11px] text-slate-500">Document de rapprochement avec vos relevés PayPal : date, référence, type, montant brut, devise, frais PayPal (si transmis), remboursements, net (si vérifiable), statut, vérification, environnement, transactions de test. Une notification IPN n'est pas une pièce comptable ; seules les lignes « Encaissement vérifié » sont des paiements authentifiés par PayPal.</p>
    </div>
  );
};

const IpnJournal = () => {
  const [mode, setMode] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (m = mode) => { setLoading(true); try { setData((await adminJournalAPI.ipn(m)).data); } catch { toast.error('Journal IPN indisponible'); } finally { setLoading(false); } };
  useEffect(() => { load(mode); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = data?.summary || {};
  return (
    <Card data-testid="ipn-journal-card">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-lg flex items-center gap-2"><Receipt className="h-5 w-5 text-emerald-700" />Journal des notifications PayPal (IPN)</CardTitle>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border bg-white p-1" data-testid="ipn-mode-filter">
            {['all', 'live', 'sandbox'].map((m) => (
              <button key={m} type="button" onClick={() => setMode(m)} data-testid={`ipn-mode-${m}`} className={`px-3 py-1 rounded-full text-xs font-semibold ${mode === m ? 'bg-emerald-600 text-white' : 'text-slate-600'}`}>{m === 'all' ? 'Tous' : m.toUpperCase()}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => load()} data-testid="ipn-refresh-btn"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <Stat label="Notifications" value={s.total} testId="ipn-stat-total" />
          <Stat label="Vérifiées PayPal" value={s.verifiees} tone="emerald" testId="ipn-stat-verified" />
          <Stat label="Rejetées" value={s.rejetees} tone="red" testId="ipn-stat-rejected" />
          <Stat label="Traitées" value={s.traitees} tone="emerald" testId="ipn-stat-processed" />
          <Stat label="Doublons" value={s.doublons} tone="amber" testId="ipn-stat-dup" />
          <Stat label="LIVE / Sandbox" value={`${s.live ?? 0} / ${s.sandbox ?? 0}`} testId="ipn-stat-mode" />
          <Stat label="Marquées test" value={s.transactions_test} tone="amber" testId="ipn-stat-test" />
        </div>
        <CsvExport mode={mode} />
        {data && data.mode_courant === 'sandbox' && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2" data-testid="ipn-sandbox-notice"><FlaskConical className="h-4 w-4" />Environnement actuellement en mode SANDBOX.</div>}
        <div className="overflow-x-auto">
          <Table data-testid="ipn-table">
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Transaction</TableHead><TableHead>Abonnement</TableHead><TableHead className="text-right">Montant</TableHead><TableHead>Type</TableHead><TableHead>Env.</TableHead><TableHead>Vérification</TableHead><TableHead>Résultat</TableHead><TableHead>Payeur</TableHead></TableRow></TableHeader>
            <TableBody>
              {(data?.items || []).length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 text-sm" data-testid="ipn-empty">Aucune notification PayPal enregistrée{mode !== 'all' ? ` en ${mode.toUpperCase()}` : ''}.</TableCell></TableRow>}
              {(data?.items || []).map((i) => (
                <TableRow key={i.id} data-testid={`ipn-row-${i.id}`} className={i.duplicate || i.verification === 'INVALID' ? 'bg-red-50/40' : ''}>
                  <TableCell className="whitespace-nowrap text-xs">{fmt(i.received_at)}</TableCell>
                  <TableCell className="font-mono text-xs">{i.txn_id || '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{i.subscription_id || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm">{eur(i.amount, i.currency)}</TableCell>
                  <TableCell className="text-xs">{TYPE_LABEL[i.type_achat] ?? i.type_achat}</TableCell>
                  <TableCell><Badge variant={i.mode === 'sandbox' ? 'outline' : 'default'} className={i.mode === 'sandbox' ? 'border-amber-400 text-amber-800' : 'bg-slate-800'}>{i.mode.toUpperCase()}</Badge></TableCell>
                  <TableCell><VerifBadge v={i.verification} /></TableCell>
                  <TableCell className="text-xs">{i.result}{i.transaction_test && <span className="ml-1 text-amber-700">(test)</span>}</TableCell>
                  <TableCell className="text-xs">{i.payer_email || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-xs text-slate-500">Lecture seule. Les notifications brutes sont conservées intégralement (audit) ; aucune donnée bancaire n'est stockée ni affichée.</p>
      </CardContent>
    </Card>
  );
};

const EmailJournal = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const load = async () => { setBusy('load'); try { setData((await adminJournalAPI.emails()).data); } catch { toast.error('Journal emails indisponible'); } finally { setBusy(''); } };
  useEffect(() => { load(); }, []);
  const retry = async (id) => { setBusy(id); try { const r = await adminJournalAPI.retryEmail(id); r.data.ok ? toast.success('Lien sécurisé envoyé (accepté par Brevo)') : toast.error('Brevo a de nouveau refusé l\'envoi'); await load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Relance impossible'); } finally { setBusy(''); } };
  const ack = async (id) => { await adminJournalAPI.ackAlert(id); load(); };
  const s = data?.summary || {};
  const hasFailures = (s.echecs || 0) > 0;
  return (
    <Card data-testid="email-journal-card" className={hasFailures ? 'border-red-300' : ''}>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-lg flex items-center gap-2"><Mail className="h-5 w-5 text-emerald-700" />Emails d'accès client (bienvenue / activation)</CardTitle>
        <Button variant="outline" size="sm" onClick={load} data-testid="email-refresh-btn"><RefreshCw className={`h-4 w-4 ${busy === 'load' ? 'animate-spin' : ''}`} /></Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Stat label="Demandés" value={s.demandes} testId="email-stat-requested" />
          <Stat label="Acceptés par Brevo (≠ délivrés)" value={s.acceptes_non_confirmes} tone="sky" testId="email-stat-accepted" />
          <Stat label="Délivrés (confirmés)" value={s.delivres_confirmes} tone="emerald" testId="email-stat-delivered" />
          <Stat label="Rejetés (bounce / bloqué)" value={s.rejetes} tone={(s.rejetes || 0) > 0 ? 'red' : 'slate'} testId="email-stat-rejected" />
          <Stat label="En échec d'envoi" value={s.echecs_envoi} tone={(s.echecs_envoi || 0) > 0 ? 'red' : 'slate'} testId="email-stat-failed" />
          <Stat label="Inconnus / en attente" value={s.inconnus_en_attente} tone="amber" testId="email-stat-unknown" />
        </div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
          <span data-testid="email-webhook-status">{s.webhook_brevo_configure ? <ShieldCheck className="inline h-3.5 w-3.5 text-emerald-600 mr-1" /> : <ShieldAlert className="inline h-3.5 w-3.5 text-amber-600 mr-1" />}Webhook Brevo {s.webhook_brevo_configure ? `prêt (${s.webhook_evenements_recus ?? 0} événement(s) reçu(s))` : 'non configuré'} — sans lui, « accepté » ne vaut pas preuve de délivrance.</span>
          {s.webhook_url && (
            <span className="basis-full" data-testid="email-webhook-url">URL à déclarer dans Brevo (Transactionnel → Paramètres → Webhooks ; événements : délivré, hard bounce, soft bounce, bloqué, email invalide, spam, différé, erreur) : <code className="break-all select-all">{s.webhook_url}</code></span>
          )}
          <span data-testid="email-retry-planned">Relances automatiques planifiées : {s.relances_planifiees ?? 0} (max 3 tentatives, lien sécurisé à usage unique, aucun mot de passe transmis).</span>
        </div>
        {(data?.alerts || []).length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-2" data-testid="email-alerts">
            {data.alerts.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 text-sm text-red-900">
                <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{fmt(a.created_at)} — {a.to_email} ({a.email_type}) : {a.reason}</span>
                <Button size="sm" variant="ghost" onClick={() => ack(a.id)} data-testid={`alert-ack-${a.id}`}>Vu</Button>
              </div>
            ))}
          </div>
        )}
        <div className="overflow-x-auto">
          <Table data-testid="email-table">
            <TableHeader><TableRow><TableHead>Mis à jour</TableHead><TableHead>Destinataire</TableHead><TableHead>Type</TableHead><TableHead>Origine</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Tentatives</TableHead><TableHead>Prochaine relance</TableHead><TableHead>Dernière erreur</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {(data?.items || []).length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 text-sm" data-testid="email-empty">Aucun email d'accès journalisé pour l'instant.</TableCell></TableRow>}
              {(data?.items || []).map((i) => (
                <TableRow key={i.id} data-testid={`email-row-${i.id}`}>
                  <TableCell className="whitespace-nowrap text-xs">{fmt(i.updated_at)}</TableCell>
                  <TableCell className="text-xs">{i.to_email}</TableCell>
                  <TableCell className="text-xs">{i.email_type}</TableCell>
                  <TableCell className="text-xs">{i.source}</TableCell>
                  <TableCell><EmailBadge s={i.status} /></TableCell>
                  <TableCell className="text-right tabular-nums text-xs">{i.attempts}/{i.max_attempts}</TableCell>
                  <TableCell className="text-xs">{i.status === 'failed' ? fmt(i.next_retry_at) : '—'}</TableCell>
                  <TableCell className="text-xs max-w-[220px] truncate" title={i.last_error || ''}>{i.last_error || '—'}</TableCell>
                  <TableCell>{i.status !== 'delivered' && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => retry(i.id)} data-testid={`email-retry-${i.id}`}>{busy === i.id ? '…' : 'Relancer (lien sécurisé)'}</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AdminPaymentsLogPage() {
  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Journal paiements &amp; emails</h1>
          <p className="text-sm text-muted-foreground mt-1">Traçabilité d'audit — réservé à l'administrateur. Rien n'est supprimé de l'historique.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/commercial"><Button variant="ghost" size="sm">Dashboard commercial</Button></Link>
          <Link to="/admin/paypal-sandbox"><Button variant="ghost" size="sm"><FlaskConical className="h-4 w-4 mr-1" />Sandbox</Button></Link>
        </div>
      </div>
      <EmailJournal />
      <IpnJournal />
    </div>
  );
}
